import React, { useState, useRef, useCallback } from 'react';
import { FileIcon } from '../../assets/icons';

const MAX_SIZE = 10 * 1024 * 1024;

const FileUpload = ({
  onFileSelect,
  onTextExtracted,
  className = '',
  disabled = false,
  ...props
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);



  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setIsDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  }, [disabled]);

  const extractTextFromPdf = async (file) => {
    if (!window.pdfjsLib) await loadPdfJs();

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      setProgress((i / pdf.numPages) * 100);
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      fullText += textContent.items.map(item => item.str).join(' ') + '\n';
    }

    return fullText.trim();
  };

  const loadPdfJs = () =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });


  const handleFileSelect = useCallback(async (file) => {
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      return;
    }

    if (file.size > MAX_SIZE) {
      alert('PDF size must be under 10MB.');
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    try {
      const text = await extractTextFromPdf(file);
      onTextExtracted?.(text);
      onFileSelect?.(file);
    } catch (err) {
      alert('Failed to extract text from PDF.');
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  }, [onFileSelect, onTextExtracted]);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const openFileDialog = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`
          relative border-2 border-dashed rounded-2xl p-8 text-center transition-all
          ${isDragOver ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:bg-gray-50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled}
          {...props}
        />

        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-gray-100 rounded-full">
            <FileIcon width="48px" height="48px" />
          </div>

          <div>
            <h3 className="font-semibold text-lg">
              {isDragOver ? 'Drop PDF here' : 'Upload PDF'}
            </h3>
            <p className="text-sm text-gray-600">
              Drag & drop or <span className="text-red-600 font-medium">browse</span>
            </p>
            <p className="text-xs text-gray-500">
              PDF only · Max 10MB
            </p>
          </div>
        </div>

        {isProcessing && (
          <div className="absolute inset-0 bg-white/90 rounded-2xl flex items-center justify-center">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto" />
              <p className="text-sm font-medium">
                Extracting PDF… {Math.round(progress)}%
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
